import { motion, AnimatePresence } from 'motion/react';
import { X, Share2, ChevronLeft, ChevronRight, Package, Shield, Zap, Truck } from 'lucide-react';
import { useLayoutEffect, useRef, useState } from 'react';
import { orderViaWhatsApp, shareProduct } from '../utils/whatsapp';
import { toast } from 'sonner';
import '../accessoriesStyles/product-modal.css';
import { ImageWithFallback } from './figma/ImageWithFallback.jsx';

// Mock additional product data
const getProductDetails = (product) => {
  const images = product.images || [product.image];
  const hasMultipleImages = images.length > 1;
  
  return {
    images: hasMultipleImages ? images : [product.image, product.image, product.image],
    hasMultipleImages,
    description: product.description || `Experience premium quality with the ${product.name}. Crafted by ${product.brand}, this ${product.category.toLowerCase()} accessory delivers exceptional performance and style.`,
    features: product.features || [
      'Premium build quality',
      'Advanced technology',
      'Long-lasting durability',
      'Sleek modern design',
    ],
    specifications: product.specifications || {
      Brand: product.brand,
      Category: product.category,
      'Product Type': product.productType || product.name,
      'Price': `₹${product.price.toLocaleString('en-IN')}`,
      'Stock Status': product.inStock ? 'In Stock' : 'Out of Stock',
      'Warranty': '1 Year',
      'Compatibility': 'Universal',
      'Color': product.colors ? product.colors.join(', ') : 'Multiple Options',
    },
  };
};

export function ProductDetailModal({ product, isOpen, onClose }) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const touchStartRef = useRef({ x: 0, y: 0 });

  useLayoutEffect(() => {
    setCurrentImageIndex(0);
  }, [product?.id, isOpen]);

  if (!product) return null;

  const details = getProductDetails(product);
  const isComingSoon = product.comingSoon || !product.inStock;
  const safeImageIndex = currentImageIndex % details.images.length;

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % details.images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + details.images.length) % details.images.length);
  };

  const handleTouchStart = (e) => {
    const touch = e.changedTouches?.[0];
    if (!touch) return;
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
  };

  const handleTouchEnd = (e) => {
    if (!details.hasMultipleImages) return;

    const touch = e.changedTouches?.[0];
    if (!touch) return;

    const deltaX = touch.clientX - touchStartRef.current.x;
    const deltaY = touch.clientY - touchStartRef.current.y;
    const absX = Math.abs(deltaX);
    const absY = Math.abs(deltaY);
    const swipeThreshold = 35;

    // Only react to clear horizontal swipes so vertical modal scrolling still works.
    if (absX < swipeThreshold || absX <= absY) return;

    if (deltaX < 0) {
      nextImage();
    } else {
      prevImage();
    }
  };

  const handleShare = async () => {
    const success = await shareProduct(product);
    if (success) {
      toast.success('Product shared successfully!');
    }
  };

  const handleWhatsAppOrder = () => {
    if (isComingSoon) {
      toast.info('This product is coming soon! We\'ll notify you when it\'s available.');
      return;
    }
    orderViaWhatsApp(product);
    toast.success('Opening WhatsApp...');
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="modal-backdrop"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="modal-container"
          >
            <div className="modal-content">
              {/* Header */}
              <div className="modal-header">
                <div className="modal-header-left">
                  <div className="modal-brand-badge">
                    {product.brand}
                  </div>
                  <div className={`modal-stock-indicator ${isComingSoon ? 'coming-soon' : ''}`}>
                    <Package className="modal-stock-icon" />
                    <span className="modal-stock-text">
                      {product.comingSoon ? 'Coming Soon' : product.inStock ? 'In Stock' : 'Out of Stock'}
                    </span>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="modal-close-button"
                >
                  <X className="modal-close-icon" />
                </button>
              </div>

              {/* Content */}
              <div className="modal-body">
                <div className="modal-grid">
                  {/* Left: Image Gallery */}
                  <div className="modal-image-section">
                    <div
                      className="modal-image-container"
                      onTouchStart={handleTouchStart}
                      onTouchEnd={handleTouchEnd}
                    >
                      <ImageWithFallback
                        key={details.images[safeImageIndex]}
                        src={details.images[safeImageIndex]}
                        alt={product.name}
                        className="modal-image"
                      />
                      
                      {/* Navigation Arrows */}
                      {details.hasMultipleImages && (
                        <>
                          <button
                            onClick={prevImage}
                            className="modal-image-nav modal-image-nav-prev"
                          >
                            <ChevronLeft className="modal-image-nav-icon" />
                          </button>
                          <button
                            onClick={nextImage}
                            className="modal-image-nav modal-image-nav-next"
                          >
                            <ChevronRight className="modal-image-nav-icon" />
                          </button>
                        </>
                      )}

                      {/* Image Indicators */}
                      {details.hasMultipleImages && (
                        <div className="modal-image-indicators">
                          {details.images.map((_, index) => (
                            <button
                              key={index}
                              onClick={() => setCurrentImageIndex(index)}
                              className={`modal-image-indicator ${index === safeImageIndex ? 'active' : ''}`}
                            />
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Trust Badges */}
                    <div className="modal-trust-badges">
                      <div className="modal-trust-badge">
                        <Shield className="modal-trust-icon blue" />
                        <span className="modal-trust-text">Authentic Guarantee</span>
                      </div>
                      <div className="modal-trust-badge">
                        <Truck className="modal-trust-icon green" />
                        <span className="modal-trust-text">Fast Delivery</span>
                      </div>
                      <div className="modal-trust-badge">
                        <Zap className="modal-trust-icon yellow" />
                        <span className="modal-trust-text">Quick Support</span>
                      </div>
                    </div>
                  </div>

                  {/* Right: Product Info */}
                  <div className="modal-info-section">
                    <div className="modal-product-header">
                      <p className="modal-product-category">
                        {product.category}
                      </p>
                      <h2 className="modal-product-title">
                        {product.name}
                      </h2>
                      <div className="modal-product-price-container">
                        {product.originalPrice && (
                          <p className="modal-product-original-price">
                            M.R.P. ₹{product.originalPrice.toLocaleString('en-IN')}
                          </p>
                        )}
                        <p className="modal-product-price">
                          {product.originalPrice && 'Offer '}₹{product.price.toLocaleString('en-IN')}
                        </p>
                      </div>
                    </div>

                    {/* Description */}
                    <div className="modal-info-card">
                      <h3 className="modal-info-card-title">
                        About this product
                      </h3>
                      <p className="modal-info-card-text">
                        {details.description}
                      </p>
                    </div>

                    {/* Key Features */}
                    <div className="modal-info-card">
                      <h3 className="modal-info-card-title">
                        Key Features
                      </h3>
                      <ul className="modal-features-list">
                        {details.features.map((feature, index) => (
                          <li key={index} className="modal-feature-item">
                            <span className="modal-feature-bullet" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Compatible Models (for iPad case) */}
                    {product.compatibleModels && (
                      <div className="modal-info-card">
                        <h3 className="modal-info-card-title">
                          Compatible Models
                        </h3>
                        <ul className="modal-features-list">
                          {product.compatibleModels.map((model, index) => (
                            <li key={index} className="modal-feature-item">
                              <span className="modal-feature-bullet" />
                              {model}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Specifications */}
                    <div className="modal-info-card">
                      <h3 className="modal-info-card-title">
                        Specifications
                      </h3>
                      <div className="modal-specs-grid">
                        {Object.entries(details.specifications).map(([key, value]) => (
                          <div key={key} className="modal-spec-item">
                            <span className="modal-spec-label">
                              {key}
                            </span>
                            <span className="modal-spec-value">
                              {value}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating Action Bar */}
              <div className="modal-footer">
                <div className="modal-footer-content">
                  {/* Share Button */}
                  <button
                    onClick={handleShare}
                    className="modal-share-button"
                  >
                    <Share2 className="modal-share-icon" />
                    Share
                  </button>

                  {/* WhatsApp Order Button */}
                  <button
                    onClick={handleWhatsAppOrder}
                    className={`modal-whatsapp-button ${isComingSoon ? 'disabled' : ''}`}
                    disabled={isComingSoon}
                  >
                    <svg
                      className="modal-whatsapp-icon"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                    </svg>
                    {isComingSoon ? 'Coming Soon' : 'Order via WhatsApp'}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
